import { useEffect, useState } from "react";
import { adminApi } from "./api";
import { currentToken, signIn, signOut } from "./auth";
import { PublicSite } from "./PublicSite";
import type { ModerationReport, Submission, SubmissionStatus } from "./types";
import "./styles.css";
import "./public-modern.css";

const statuses: SubmissionStatus[] = [
  "submitted",
  "under_review",
  "approved",
  "published",
  "rejected",
  "removed",
];

function Mark() {
  return (
    <a className="mark" href="/" aria-label="HeartSpeaks home">
      <span>Heart</span>Speaks
      <i />
    </a>
  );
}

function AdminApp() {
  const [token, setToken] = useState<string | null>(null);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [status, setStatus] = useState<SubmissionStatus>("submitted");
  const [items, setItems] = useState<Submission[]>([]);
  const [selected, setSelected] = useState<Submission | null>(null);
  const [insights, setInsights] = useState<Record<string, number>>({});
  const [reflectionHeartTotal, setReflectionHeartTotal] = useState(0);
  const [reports, setReports] = useState<ModerationReport[]>([]);
  const [error, setError] = useState("");

  useEffect(() => {
    currentToken().then((value) => {
      setToken(value);
      setLoadingAuth(false);
    });
  }, []);
  useEffect(() => {
    if (!token) return;
    adminApi
      .list(token, status)
      .then(({ items: result }) => setItems(result))
      .catch(() => setError("Could not load submissions."));
    adminApi
      .insights(token)
      .then(({ totals, reflectionHeartTotal }) => {
        setInsights(totals);
        setReflectionHeartTotal(reflectionHeartTotal);
      })
      .catch(() => undefined);
    adminApi
      .reports(token)
      .then(({ items: result }) => setReports(result))
      .catch(() => undefined);
  }, [token, status]);

  if (loadingAuth) return <main className="admin-auth">Checking session…</main>;
  if (!token) return <AdminLogin onSuccess={setToken} />;

  const save = async (action?: "publish" | "unpublish") => {
    if (!selected) return;
    try {
      const result =
        action === "publish"
          ? await adminApi.publish(token, selected.id)
          : action === "unpublish"
            ? await adminApi.unpublish(token, selected.id)
            : await adminApi.update(token, selected.id, selected);
      setSelected(result.item);
      setItems((current) => current.filter((item) => item.id !== selected.id));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Update failed.");
    }
  };

  const openReportedSubmission = async (report: ModerationReport) => {
    try {
      const result = await adminApi.get(token, report.submissionId);
      setSelected(result.item);
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Could not load voice.",
      );
    }
  };

  const resolveReport = async (report: ModerationReport) => {
    try {
      await adminApi.resolveReport(token, report.id);
      setReports((current) => current.filter((item) => item.id !== report.id));
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Could not resolve report.",
      );
    }
  };

  return (
    <div className="admin">
      <header>
        <Mark />
        <strong>Editorial desk</strong>
        <button
          className="button text"
          onClick={() => {
            signOut();
            setToken(null);
          }}
        >
          Sign out
        </button>
      </header>
      <section className="insight-strip">
        <article>
          <span>Conversation opens</span>
          <strong>{insights.conversation_opened || 0}</strong>
        </article>
        <article>
          <span>Private reflections</span>
          <strong>{insights.private_reflection_saved || 0}</strong>
        </article>
        <article>
          <span>Submissions completed</span>
          <strong>{insights.submission_completed || 0}</strong>
        </article>
        <article>
          <span>Return visits</span>
          <strong>{insights.return_visit || 0}</strong>
        </article>
        <article>
          <span>Reflection hearts</span>
          <strong>{reflectionHeartTotal}</strong>
        </article>
        <article>
          <span>Open reports</span>
          <strong>{reports.length}</strong>
        </article>
      </section>
      <div className="admin-layout">
        <aside>
          <h1>Submissions</h1>
          {statuses.map((item) => (
            <button
              className={status === item ? "active" : ""}
              onClick={() => {
                setStatus(item);
                setSelected(null);
              }}
              key={item}
            >
              {item.replace("_", " ")}
            </button>
          ))}
          {reports.length > 0 && (
            <div className="admin-reports">
              <h2>Reported reader voices</h2>
              {reports.map((report) => (
                <article key={report.id}>
                  <p>
                    {report.questionSlug} · {report.reason}
                  </p>
                  <div>
                    <button onClick={() => void openReportedSubmission(report)}>
                      Review voice
                    </button>
                    <button onClick={() => void resolveReport(report)}>
                      Resolve
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </aside>
        <main>
          <div className="inbox">
            <h2>{status.replace("_", " ")}</h2>
            {error && <p className="error">{error}</p>}
            {items.length === 0 ? (
              <p className="empty">No submissions in this state.</p>
            ) : (
              items.map((item) => (
                <button onClick={() => setSelected(item)} key={item.id}>
                  <span>
                    {item.entityType === "question_reflection"
                      ? `Question · ${item.questionSlug}`
                      : item.identityMode === "first_name"
                        ? item.firstName
                        : item.identityMode}
                  </span>
                  <p>{item.originalText.slice(0, 120)}</p>
                  <time>{new Date(item.submittedAt).toLocaleDateString()}</time>
                </button>
              ))
            )}
          </div>
          {selected && (
            <div className="review">
              <div className="review-head">
                <span>{selected.id}</span>
                <button aria-label="Close" onClick={() => setSelected(null)}>
                  ×
                </button>
              </div>
              {selected.questionSlug && (
                <p className="review-context">
                  Reader voice for Question: {selected.questionSlug}
                </p>
              )}
              <label>
                Original response
                <textarea value={selected.originalText} readOnly />
              </label>
              <label>
                Publishable version
                <textarea
                  value={selected.publicText}
                  onChange={(event) =>
                    setSelected({ ...selected, publicText: event.target.value })
                  }
                />
              </label>
              <div className="review-grid">
                <label>
                  Status
                  <select
                    value={selected.status}
                    onChange={(event) =>
                      setSelected({
                        ...selected,
                        status: event.target.value as SubmissionStatus,
                      })
                    }
                  >
                    {statuses
                      .filter((item) => item !== "published")
                      .map((item) => (
                        <option key={item}>{item}</option>
                      ))}
                  </select>
                </label>
                <label>
                  Display name
                  <input
                    value={selected.firstName || ""}
                    disabled={selected.identityMode !== "first_name"}
                    onChange={(event) =>
                      setSelected({
                        ...selected,
                        firstName: event.target.value,
                      })
                    }
                  />
                </label>
              </div>
              <label className="consent">
                <input
                  type="checkbox"
                  checked={selected.sensitivityFlag}
                  onChange={(event) =>
                    setSelected({
                      ...selected,
                      sensitivityFlag: event.target.checked,
                    })
                  }
                />
                Sensitive content
              </label>
              <label className="consent">
                <input
                  type="checkbox"
                  checked={selected.redactionFlag}
                  onChange={(event) =>
                    setSelected({
                      ...selected,
                      redactionFlag: event.target.checked,
                    })
                  }
                />
                Requires further anonymisation
              </label>
              <div className="button-row">
                <button className="button secondary" onClick={() => save()}>
                  Save review
                </button>
                {selected.status === "approved" && (
                  <button
                    className="button primary"
                    onClick={() => save("publish")}
                  >
                    {selected.entityType === "question_reflection"
                      ? "Publish reader voice"
                      : "Publish story"}
                  </button>
                )}
                {selected.status === "published" && (
                  <button
                    className="button danger"
                    onClick={() => save("unpublish")}
                  >
                    Unpublish
                  </button>
                )}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function AdminLogin({ onSuccess }: { onSuccess: (token: string) => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  return (
    <main className="admin-auth">
      <Mark />
      <form
        onSubmit={async (event) => {
          event.preventDefault();
          setError("");
          try {
            onSuccess(await signIn(email, password));
          } catch (cause) {
            setError(
              cause instanceof Error ? cause.message : "Sign in failed.",
            );
          }
        }}
      >
        <span>Private editorial access</span>
        <h1>Welcome back.</h1>
        <label>
          Email
          <input
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>
        <label>
          Password
          <input
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </label>
        <button className="button primary wide">Sign in</button>
        {error && <p className="error">{error}</p>}
      </form>
    </main>
  );
}

export default function App() {
  const path = typeof window === "undefined" ? "/" : window.location.pathname;
  return path.startsWith("/admin") ? (
    <AdminApp />
  ) : (
    <PublicSite
      initialPath={path}
      initialSearch={
        typeof window === "undefined" ? "" : window.location.search
      }
    />
  );
}
